import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';

const fallbackMenuItems = [
  { label: 'Forside', link: '/' },
  { label: 'Ydelser', link: '/ydelser' },
  { label: 'Om os', link: '/om-os' },
  { label: 'Priser', link: '/priser' },
  { label: 'Kontakt', link: '/kontakt' }
];

export default function Navbar() {
  const [menuItems, setMenuItems] = useState(fallbackMenuItems);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobilePricesOpen, setIsMobilePricesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/menu_items')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente menu');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
          setMenuItems(sorted);
        }
      })
      .catch((err) => console.error('Fejl ved hentning af menu fra CMS:', err));
  }, []);

  // Luk mobilmenuen ved sideskift
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <header className="w-full sticky top-0 z-50 bg-white shadow-md">
      {/* Top Bar med kontaktoplysninger */}
      <div className="w-full bg-[#0B192C] text-white text-xs py-2.5 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex justify-end items-center">
          <div className="flex items-center gap-6">
            <a
              href="tel:+4522289310"
              className="flex items-center gap-1.5 hover:text-[#2563EB] transition-colors whitespace-nowrap"
            >
              <Phone className="h-3.5 w-3.5 text-[#2563EB]" />
              <span className="font-semibold whitespace-nowrap">22 28 93 10</span>
            </a>
            <a
              href="mailto:kontakt@flowtl.dk"
              className="flex items-center gap-1.5 hover:text-[#2563EB] transition-colors whitespace-nowrap"
            >
              <Mail className="h-3.5 w-3.5 text-[#2563EB]" />
              <span className="font-semibold whitespace-nowrap">kontakt@flowtl.dk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Hovednavigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center py-2 shrink-0">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=95,format=auto/assets/ecc7c3d2-b2a3-4a1d-ba29-5687874f0c20/logo.jpg"
              alt="FLOW Transport & Logistik ApS Logo"
              className="h-16 sm:h-18 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu (Vises kun ved skærme >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            {menuItems.map((item, idx) => {
              const isActive =
                location.pathname === item.link ||
                (item.link !== '/' && location.pathname.startsWith(item.link));

              if (item.link === '/priser' || item.label === 'Priser') {
                return (
                  <div key={idx} className="relative group py-2">
                    <button
                      className={`flex items-center gap-1 text-sm font-semibold tracking-wide uppercase py-2 border-b-2 transition-all duration-200 cursor-pointer ${
                        isActive
                          ? 'border-[#2563EB] text-[#2563EB]'
                          : 'border-transparent text-[#0B192C] hover:text-[#2563EB] hover:border-slate-300'
                      }`}
                    >
                      <span>{item.label}</span>
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                    {/* Dropdown menu */}
                    <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-100 py-1.5 hidden group-hover:block z-50">
                      <Link
                        to="/priser?type=privat"
                        className="block px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0B192C] hover:bg-slate-50 hover:text-[#2563EB] transition-colors"
                      >
                        Privat <span className="text-[10px] font-normal text-slate-500 lowercase">(inkl. moms)</span>
                      </Link>
                      <Link
                        to="/priser?type=erhverv"
                        className="block px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0B192C] hover:bg-slate-50 hover:text-[#2563EB] transition-colors border-t border-slate-50"
                      >
                        Erhverv <span className="text-[10px] font-normal text-slate-500 lowercase">(ekskl. moms)</span>
                      </Link>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.link}
                  className={`text-sm font-semibold tracking-wide uppercase py-2 border-b-2 transition-all duration-200 ${
                    isActive
                      ? 'border-[#2563EB] text-[#2563EB]'
                      : 'border-transparent text-[#0B192C] hover:text-[#2563EB] hover:border-slate-300'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/kontakt"
              className="ml-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-wider transition-colors shadow-sm hover:shadow"
            >
              Få et tilbud
            </Link>
          </nav>

          {/* Burgermenu Knap (Vises kun ved skærme < 1200px) */}
          <div className="flex min-[1200px]:hidden items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-[#0B192C] hover:text-[#2563EB] hover:bg-slate-100 focus:outline-none transition-colors"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Åbn hovedmenu</span>
              {isOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobilmenu (Vises kun ved skærme < 1200px, når åben) */}
      <div
        className={`min-[1200px]:hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[500px] opacity-100 border-t border-slate-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
        id="mobile-menu"
      >
        <div className="px-4 pt-3 pb-6 space-y-2 bg-slate-50">
          {menuItems.map((item, idx) => {
            const isActive =
              location.pathname === item.link ||
              (item.link !== '/' && location.pathname.startsWith(item.link));

            if (item.link === '/priser' || item.label === 'Priser') {
              return (
                <div key={idx} className="space-y-1">
                  <button
                    onClick={() => setIsMobilePricesOpen(!isMobilePricesOpen)}
                    className={`flex items-center justify-between w-full px-4 py-3 rounded-md text-base font-semibold tracking-wide uppercase transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-[#2563EB]/10 text-[#2563EB]'
                        : 'text-[#0B192C] hover:bg-slate-100 hover:text-[#2563EB]'
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMobilePricesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMobilePricesOpen && (
                    <div className="pl-4 space-y-1 bg-slate-100/50 rounded-md py-1">
                      <Link
                        to="/priser?type=privat"
                        className="block px-4 py-2.5 text-sm font-semibold text-[#0B192C] hover:text-[#2563EB]"
                      >
                        Privat <span className="text-xs font-normal text-slate-500 lowercase">(inkl. moms)</span>
                      </Link>
                      <Link
                        to="/priser?type=erhverv"
                        className="block px-4 py-2.5 text-sm font-semibold text-[#0B192C] hover:text-[#2563EB]"
                      >
                        Erhverv <span className="text-xs font-normal text-slate-500 lowercase">(ekskl. moms)</span>
                      </Link>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={idx}
                to={item.link}
                className={`block px-4 py-3 rounded-md text-base font-semibold tracking-wide uppercase transition-colors ${
                  isActive
                    ? 'bg-[#2563EB]/10 text-[#2563EB]'
                    : 'text-[#0B192C] hover:bg-slate-100 hover:text-[#2563EB]'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
          <div className="pt-4 px-4">
            <Link
              to="/kontakt"
              className="block w-full text-center bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-5 py-3 rounded-md text-base font-bold uppercase tracking-wider transition-colors shadow"
            >
              Få et tilbud
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
